// src/services/learnerUpdateService.js
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  getDoc,
  limit,
  startAfter
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, auth } from './firebase';

const COLLECTIONS = {
  LEARNER_UPDATES: 'taskdata-learner-updates'
};

export class LearnerUpdateService {
  // Get current user ID
  static getCurrentUserId() {
    return auth.currentUser?.uid;
  }

  // Check if user is authenticated
  static ensureAuthenticated() {
    const userId = this.getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }
    return userId;
  }

  // Create a new update for a learner
  static async createUpdate(learnerId, updateData, attachments = []) {
    const userId = this.ensureAuthenticated();

    try {
      // Upload attachments if any
      const uploadedAttachments = [];
      for (const file of attachments) {
        const result = await this.uploadAttachment(learnerId, file);
        uploadedAttachments.push(result);
      }

      const docRef = await addDoc(collection(db, COLLECTIONS.LEARNER_UPDATES), {
        learnerId,
        content: updateData.content,
        category: updateData.category || 'general',
        priority: updateData.priority || 'normal',
        attachments: uploadedAttachments,
        createdBy: userId,
        createdAt: new Date(),
        updatedAt: new Date(),
        isEdited: false,
        reactions: [],
        status: 'active'
      });

      return docRef.id;
    } catch (error) {
      console.error('Error creating learner update:', error);
      throw new Error('Failed to create update');
    }
  }

  // Get updates for a specific learner
  static async getUpdates(learnerId, limitCount = 10, lastDoc = null) {
    this.ensureAuthenticated();

    try {
      let q = query(
        collection(db, COLLECTIONS.LEARNER_UPDATES),
        where('learnerId', '==', learnerId),
        where('status', '==', 'active'),
        orderBy('createdAt', 'desc'),
        
      );

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
        updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt
      }));
    } catch (error) {
      console.error('Error fetching learner updates:', error);
      throw new Error('Failed to fetch updates');
    }
  }

  // Get all updates for multiple learners (for dashboard view)
  static async getRecentUpdates(learnerIds = [], limitCount = 20) {
    this.ensureAuthenticated();

    try {
      let q;
      
      if (learnerIds.length > 0 && learnerIds.length <= 10) {
        q = query(
          collection(db, COLLECTIONS.LEARNER_UPDATES),
          where('learnerId', 'in', learnerIds),
          where('status', '==', 'active'),
          orderBy('createdAt', 'desc'),
          limit(limitCount)
        );
      } else {
        q = query(
          collection(db, COLLECTIONS.LEARNER_UPDATES),
          where('status', '==', 'active'),
          orderBy('createdAt', 'desc'),
          limit(limitCount)
        );
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
        updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt
      }));
    } catch (error) {
      console.error('Error fetching recent updates:', error);
      throw new Error('Failed to fetch recent updates');
    }
  }

  // Update an existing update
  static async updateUpdate(updateId, updates) {
    const userId = this.ensureAuthenticated();

    try {
      const updateRef = doc(db, COLLECTIONS.LEARNER_UPDATES, updateId);
      const updateDoc = await getDoc(updateRef);

      if (!updateDoc.exists()) {
        throw new Error('Update not found');
      }

      const updateData = updateDoc.data();
      if (updateData.createdBy !== userId) {
        throw new Error('Permission denied: You can only edit your own updates');
      }

      await updateDoc(updateRef, {
        ...updates,
        updatedAt: new Date(),
        isEdited: true
      });
    } catch (error) {
      console.error('Error updating learner update:', error);
      throw error;
    }
  }

  // Soft delete an update
  static async deleteUpdate(updateId) {
    const userId = this.ensureAuthenticated();

    try {
      const updateRef = doc(db, COLLECTIONS.LEARNER_UPDATES, updateId);
      const updateDoc = await getDoc(updateRef);

      if (!updateDoc.exists()) {
        throw new Error('Update not found');
      }

      const updateData = updateDoc.data();
      if (updateData.createdBy !== userId) {
        throw new Error('Permission denied: You can only delete your own updates');
      }

      await updateDoc(updateRef, {
        status: 'deleted',
        deletedAt: new Date(),
        deletedBy: userId
      });
    } catch (error) {
      console.error('Error deleting learner update:', error);
      throw error;
    }
  }

  // Upload attachment
  static async uploadAttachment(learnerId, file) {
    this.ensureAuthenticated();

    try {
      const allowedTypes = [
        'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif',
        'application/pdf',
        'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      const maxSize = 10 * 1024 * 1024; // 10MB

      if (!allowedTypes.includes(file.type)) {
        throw new Error('File type not supported');
      }

      if (file.size > maxSize) {
        throw new Error('File size must be less than 10MB');
      }

      const timestamp = Date.now();
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `${timestamp}_${sanitizedName}`;
      const storageRef = ref(storage, `learner-updates/${learnerId}/${fileName}`);

      const metadata = {
        contentType: file.type,
        customMetadata: {
          uploadedBy: this.getCurrentUserId(),
          uploadedAt: new Date().toISOString(),
          originalName: file.name
        }
      };

      const snapshot = await uploadBytes(storageRef, file, metadata);
      const downloadURL = await getDownloadURL(snapshot.ref);

      return {
        url: downloadURL,
        path: snapshot.ref.fullPath,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size
      };
    } catch (error) {
      console.error('Error uploading attachment:', error);
      throw new Error(error.message || 'Failed to upload attachment');
    }
  }

  // Delete attachment
  static async deleteAttachment(attachmentPath) {
    this.ensureAuthenticated();

    try {
      const attachmentRef = ref(storage, attachmentPath);
      await deleteObject(attachmentRef);
    } catch (error) {
      console.error('Error deleting attachment:', error);
      throw new Error('Failed to delete attachment');
    }
  }

  // Get update count for a learner
  static async getUpdateCount(learnerId) {
    this.ensureAuthenticated();

    try {
      const q = query(
        collection(db, COLLECTIONS.LEARNER_UPDATES),
        where('learnerId', '==', learnerId),
        where('status', '==', 'active')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error) {
      console.error('Error getting update count:', error);
      return 0;
    }
  }
}

export default LearnerUpdateService;